import React from 'react';

/**
 * Reusable pagination component for report tables.
 * Usage:
 *   const { paginatedData, PaginationControls } = useReportPagination(filteredData);
 *   // Render paginatedData in table
 *   // Render <PaginationControls /> below table
 */
export function useReportPagination(data, defaultPerPage = 25) {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(defaultPerPage);

  // Reset page when data changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [data.length]);

  const totalPages = Math.ceil(data.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = data.slice(startIndex, startIndex + itemsPerPage);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const getPageNumbers = () => {
    const pages = [];
    const max = 5;
    let start = Math.max(1, currentPage - Math.floor(max / 2));
    let end = Math.min(totalPages, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const PaginationControls = () => {
    if (data.length <= itemsPerPage) return null;
    return (
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between" data-testid="report-pagination">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>Show</span>
          <select
            value={itemsPerPage}
            onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            className="px-2 py-1 border border-gray-300 rounded"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span>per page | {startIndex + 1}-{Math.min(startIndex + itemsPerPage, data.length)} of {data.length}</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
            Previous
          </button>
          {getPageNumbers().map(page => (
            <button key={page} onClick={() => goToPage(page)}
              className={`px-3 py-1 border rounded text-sm ${page === currentPage ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 hover:bg-gray-100'}`}>
              {page}
            </button>
          ))}
          <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}
            className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed">
            Next
          </button>
        </div>
        <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
      </div>
    );
  };

  return { paginatedData, PaginationControls, currentPage, totalPages, startIndex };
}
